import { ReactNode, useState } from 'react';
import { Canvas } from 'fabric';

import {
  SUB_MENU_PORTAL_ID,
  CONTENT_ELEMENT_ID,
  CURSOR_ELEMENT_ID,
  BRUSH_THICKNESS_ELEMENT_ID,
} from '../canvas/draw-canvas.constants';
import CustomBackgroundToolbar from './edit-draw-canvas-toolbar';
import * as Styled from '../canvas/draw.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { PStyled } from 'approot/shared/styles/typography.style';
import { NegativeButton } from 'approot/shared/styles/buttons';
import type { InteractiveSubActivityFragment } from '../../../lesson-builder.graphql.generated';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  DrawContextProvider,
  useDrawCanvasContext,
} from '../canvas/draw-canvas.context';
import DrawCanvasInstruction from '../canvas/draw-canvas-instruction';
import { useDrawFontLoader } from '../canvas/use-draw-font-loader';
import CustomCursor from '../canvas/draw-canvas-custom-cursor';
import { DrawData } from '../draw.constants';
import { removeAllObjectsForEditor } from './remove-all-objects-for-editor';
import { useDrawCanvas } from '../canvas/use-draw-canvas';
import { LANGUAGE_CODE_EN_AU } from 'global/constants';

const CANVAS_CONTAINER_ID = 'canvas-container';
const FABRIC_ELEMENT_ID = 'fabric-container';

type Props = {
  id: string;
  type: string;
  imageUrl?: string | null;
  dimensionsData: {
    width: number;
    height: number;
  };
  subActivityProperties?: InteractiveSubActivityFragment | null;
  drawingData?: DrawData;
  isVisible: boolean;
  onSave?: (content: DrawData) => void;
  children: (renderProps: {
    canvas: Canvas | null;
    canvasContent: ReactNode;
    isCanvasReady: boolean;
    getCanvasData: () => DrawData | null;
  }) => ReactNode;
};

const EditDrawCanvas = ({
  id,
  type,
  imageUrl,
  dimensionsData,
  subActivityProperties,
  drawingData,
  onSave,
  children,
}: Props) => {
  const { toolSet, rotateCursor } = useDrawCanvasContext();
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(
    null
  );

  const {
    canvas,
    history,
    isInitialised,
    hasDrawing,
    historyCount,
    limit,
    isCursorClosed,
    getCanvasData,
    increaseZoom,
    decreaseZoom,
    undo,
    redo,
  } = useDrawCanvas({
    id,
    type,
    imageUrl,
    dimensionsData,
    subActivityProperties,
    questionData: drawingData,
    isCreateMode: true,
    canvasElement,
    language: LANGUAGE_CODE_EN_AU,
    onSave,
  });

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  return (
    <>
      {children({
        canvas: canvas,
        isCanvasReady: isInitialised,
        getCanvasData: getCanvasData,
        canvasContent: (
          <Styled.Draw>
            {isVisible && (
              <ActivityBuilderModal
                isVisible
                title="Clear all"
                headerTitle="Clear all"
                actions={
                  <NegativeButton
                    onClick={() => {
                      removeAllObjectsForEditor(canvas);
                      history?.saveCanvasState();
                      closeModal();
                    }}
                  >
                    Clear all
                  </NegativeButton>
                }
                onClose={closeModal}
              >
                <PStyled>Are you sure you want to clear all canvas?</PStyled>
              </ActivityBuilderModal>
            )}
            <Styled.DrawBottomLeft>
              {hasDrawing && (
                <SecondaryButton type="button" onClick={openModal}>
                  Clear All
                </SecondaryButton>
              )}
            </Styled.DrawBottomLeft>
            <Styled.DrawContent id={CONTENT_ELEMENT_ID}>
              <Styled.DrawCursorContainer id={CURSOR_ELEMENT_ID}>
                <CustomCursor
                  isCursorClosed={isCursorClosed}
                  tool={toolSet?.tool}
                  rotateCursor={rotateCursor}
                />
              </Styled.DrawCursorContainer>
              <div id={BRUSH_THICKNESS_ELEMENT_ID} />
              <Styled.DrawInterface $isInitialised={isInitialised}>
                <DrawCanvasInstruction
                  id={id}
                  type={type}
                  subActivityProperties={subActivityProperties}
                />
                {limit.isLimitReached ? (
                  <Styled.DrawBanner $isError>
                    You've reached the drawing limit! Please clear some space to
                    keep drawing. Any changes beyond this point may not be
                    saved.
                  </Styled.DrawBanner>
                ) : limit.isWarningReached ? (
                  <Styled.DrawBanner $isError={false}>
                    You're almost at the limit of how much you can draw! Try
                    finishing up or clearing some space to keep creating.
                  </Styled.DrawBanner>
                ) : null}
                <Styled.DrawCanvas id={CANVAS_CONTAINER_ID}>
                  <canvas
                    id={FABRIC_ELEMENT_ID}
                    ref={setCanvasElement}
                  ></canvas>
                </Styled.DrawCanvas>
              </Styled.DrawInterface>
              <Styled.DrawSubMenu id={SUB_MENU_PORTAL_ID}></Styled.DrawSubMenu>

              <CustomBackgroundToolbar
                canvas={canvas}
                canUndo={
                  historyCount.position - 1 > 0 && historyCount.stackLength > 0
                }
                canRedo={historyCount.position < historyCount.stackLength}
                onUndo={undo}
                onRedo={redo}
                increaseZoom={increaseZoom}
                decreaseZoom={decreaseZoom}
              />
            </Styled.DrawContent>
          </Styled.Draw>
        ),
      })}
    </>
  );
};

const EditDrawCanvasWithContext = (props: Props) => {
  const isFontsLoaded = useDrawFontLoader();

  return props.isVisible && isFontsLoaded ? (
    <DrawContextProvider>
      <EditDrawCanvas {...props} />
    </DrawContextProvider>
  ) : null;
};

export default EditDrawCanvasWithContext;
